'use client';
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, Suspense, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { cohesion, formatMonarchEvaluations, formatPreampExperience, isNullish, monarch, normalizeError, } from '@redventures/cohesion-utils';
export const CohesionContext = createContext(undefined);
export function CohesionProvider({ children, components = {}, }) {
    const hasInitialized = useRef(false);
    const hasError = useRef(false);
    const hasPreampError = useRef(false);
    // Cohesion
    const hasTagularContext = useRef(false);
    // Monarch
    const [isLoading, setIsLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [result, setResult] = useState(null);
    const [rules, setRules] = useState({});
    const [slots, setSlots] = useState({});
    const [monarchPreampPlacements, setMonarchPreampPlacements] = useState({});
    const monarchPreampAssets = useMemo(() => [...new Set(Object.values(monarchPreampPlacements))], [monarchPreampPlacements]);
    // Preamp
    const [preampIsLoading, setPreampIsLoading] = useState(true);
    const [preampErr, setPreampErr] = useState(null);
    const [preampDecision, setPreampDecision] = useState(null);
    const [preampPlacements, setPreampPlacements] = useState({});
    const preampAssets = useMemo(() => [...new Set(Object.values(preampPlacements))], [preampPlacements]);
    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        else {
            hasInitialized.current = true;
        }
        // Monarch
        cohesion('monarch:done', (monarchErr, monarchResult) => {
            let newRules = {};
            const newSlots = {};
            const newPreampPlacements = {};
            const normalizedErr = normalizeError(monarchErr);
            if (normalizedErr) {
                hasError.current = true;
                console.error('[cohesion-utils-react] Monarch Error', normalizedErr);
            }
            if (monarchResult) {
                newRules = formatMonarchEvaluations(monarchResult) || {};
            }
            if (newRules.trafficFlow?.returnType === 'experience') {
                const experienceValues = newRules.trafficFlow.value.experienceValues ||
                    [];
                for (const experienceValue of experienceValues) {
                    newSlots[experienceValue.slot.customId] = experienceValue;
                }
            }
            if (newRules.trafficFlow?.returnType === 'preampExperience' ||
                newRules.trafficFlow?.smartReturnType === 'preampExperience') {
                const preampExperience = formatPreampExperience(newRules.trafficFlow.value);
                if (preampExperience?.placements) {
                    for (const placement of preampExperience?.placements) {
                        newPreampPlacements[placement.placementName] = placement;
                        newPreampPlacements[placement.placementId] = placement;
                    }
                }
            }
            setErr(normalizedErr);
            setResult(monarchResult);
            setIsLoading(false);
            setRules((oldRules) => ({ ...oldRules, ...newRules }));
            setSlots((oldSlots) => ({ ...oldSlots, ...newSlots }));
            setMonarchPreampPlacements((oldPreampPlacements) => ({
                ...oldPreampPlacements,
                ...newPreampPlacements,
            }));
        });
        // Preamp
        cohesion('preamp:done', (newPreampDecision) => {
            let newPreampPlacements = {};
            if (newPreampDecision.placements) {
                for (const placement of newPreampDecision.placements) {
                    newPreampPlacements[placement.placementName] = placement;
                    newPreampPlacements[placement.placementId] = placement;
                }
            }
            setPreampDecision(newPreampDecision);
            setPreampIsLoading(false);
            setPreampPlacements((oldPreampPlacements) => ({
                ...oldPreampPlacements,
                ...newPreampPlacements,
            }));
        });
        cohesion('preamp:error', (err) => {
            hasPreampError.current = true;
            const normalizedErr = normalizeError(err);
            console.error('[cohesion-utils-react] Preamp Error', normalizedErr);
            setPreampErr(normalizedErr);
            setPreampIsLoading(false);
        });
        // Cohesion
        cohesion('error', () => {
            hasError.current = true;
            hasPreampError.current = true;
            console.error('[cohesion-utils-react] Cohesion failed to load');
            // Monarch
            setErr({ message: 'Cohesion failed to load' });
            setResult(null);
            setIsLoading(false);
            // Preamp
            setPreampErr({ message: 'Cohesion failed to load' });
            setPreampIsLoading(false);
        });
        cohesion('tagular:contextCreated', ({ autoDecision }) => {
            if (!hasTagularContext.current) {
                // Wait for Tagular context to be created during initial page load.
                hasTagularContext.current = true;
            }
            else if (autoDecision) {
                // Reset `isLoading` on soft navigations after the initial page load.
                // IMPORTANT: Only reset when _not_ in an errored state! Otherwise,
                // you'll get stuck loading until the next hard navigation.
                if (!hasError.current) {
                    setIsLoading(true);
                }
                if (!hasPreampError.current) {
                    setPreampIsLoading(true);
                }
            }
        });
    }, []);
    const contextValue = {
        // Monarch
        err,
        result,
        isLoading,
        rules,
        slots,
        monarchPreampPlacements,
        monarchPreampAssets,
        components,
        // Preamp
        preampErr,
        preampIsLoading,
        preampDecision,
        preampPlacements,
        preampAssets,
    };
    return (_jsx(CohesionContext.Provider, { value: contextValue, children: children }));
}
// ------------------------------------------------------------
// HOOKS
// ------------------------------------------------------------
/**
 * Get Monarch evaluations.
 */
export function useMonarch() {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarch cannot be used outside of CohesionProvider');
    }
    return {
        err: context.err,
        result: context.result,
        isLoading: context.isLoading,
        rules: context.rules,
        slots: context.slots,
        preampPlacements: context.monarchPreampPlacements,
        preampAssets: context.monarchPreampAssets,
    };
}
/**
 * Get value for a Monarch Rule.
 */
export function useMonarchRule(ruleCustomId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchRule cannot be used outside of CohesionProvider');
    }
    const rule = context?.rules[ruleCustomId] ?? null;
    const value = useMemo(() => {
        const isReturnTypeAllowed = !options.returnTypes ||
            options.returnTypes.includes(rule?.returnType || '') ||
            options.returnTypes.includes(rule?.smartReturnType || '');
        if (!isNullish(rule?.value) && isReturnTypeAllowed) {
            return rule.value;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [rule, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        rule,
        value,
    };
}
/**
 * Send a new request for a Monarch Rule or Rule Set.
 */
export function useMonarchRequest(requestType, customId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchRequest cannot be used outside of CohesionProvider');
    }
    const hasInitialized = useRef(false);
    const [err, setErr] = useState(null);
    const [result, setResult] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        if (!options.skip) {
            monarch(requestType, customId, options.payload ?? {}, options.params ?? {}, (newErr, newResult) => {
                setErr(normalizeError(newErr));
                setResult(newResult);
                setIsLoading(false);
            });
        }
    }, [options.skip]);
    useEffect(() => {
        if (hasInitialized.current) {
            return;
        }
        else {
            hasInitialized.current = true;
        }
        cohesion('error', () => {
            setErr({ message: 'Cohesion failed to load' });
            setResult(null);
            setIsLoading(false);
        });
    }, []);
    const value = useMemo(() => {
        if (requestType === 'rule' && !isNullish(result?.[customId])) {
            return result?.[customId];
        }
        if (requestType === 'ruleset' && Object.keys(result ?? {}).length > 0) {
            return result;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err,
                isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [result, customId, options, err, isLoading]);
    return {
        err,
        result,
        isLoading,
        value,
    };
}
/**
 * Get data for a Monarch Slot.
 */
export function useMonarchSlot(slotCustomId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchSlot cannot be used outside of CohesionProvider');
    }
    const experienceValue = context.slots[slotCustomId] ?? null;
    const data = useMemo(() => {
        const variant = experienceValue?.value ?? null;
        const isComponentAllowed = !options.components ||
            options.components.includes(variant?.componentCustomId);
        if (variant?.data && isComponentAllowed) {
            return variant.data;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [experienceValue, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        result: experienceValue,
        data,
    };
}
/**
 * Get data from a <MonarchSlot> context.
 */
export function useMonarchSlotContext(options = {}) {
    const context = useContext(MonarchSlotContext);
    if (!context) {
        throw new Error('useMonarchSlotContext cannot be used outside of a MonarchSlot');
    }
    const data = useMemo(() => {
        if (context.result?.value?.data) {
            return context.result.value?.data;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [context.result, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        result: context.result,
        data,
    };
}
/**
 * Get data for a Monarch Preamp Placement.
 */
export function useMonarchPreampPlacement(placementNameOrId, options = {}) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('useMonarchPreampPlacement cannot be used outside of CohesionProvider');
    }
    const asset = context.monarchPreampPlacements[placementNameOrId] ?? null;
    const data = useMemo(() => {
        const componentCustomId = (options.componentId instanceof Function
            ? options.componentId(asset)
            : asset?.assetName) ?? '';
        const isComponentAllowed = !options.components || options.components.includes(componentCustomId);
        if (asset?.metadata && isComponentAllowed) {
            return asset.metadata;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [asset, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        asset,
        data,
    };
}
/**
 * Get data from a <MonarchPreampPlacement> context.
 */
export function useMonarchPreampPlacementContext(options = {}) {
    const context = useContext(MonarchPreampPlacementContext);
    if (!context) {
        throw new Error('useMonarchPreampPlacementContext cannot be used outside of a MonarchPreampPlacement');
    }
    const data = useMemo(() => {
        if (context.asset?.metadata) {
            return context.asset.metadata;
        }
        if (options.default instanceof Function) {
            return (options.default({
                err: context.err,
                isLoading: context.isLoading,
            }) ?? null);
        }
        return options.default ?? null;
    }, [context.asset, options, context.err, context.isLoading]);
    return {
        err: context.err,
        isLoading: context.isLoading,
        asset: context.asset,
        data,
    };
}
/**
 * Get Preamp decisions.
 */
export function usePreamp() {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('usePreamp cannot be used outside of CohesionProvider');
    }
    return {
        preampErr: context.preampErr,
        preampIsLoading: context.preampIsLoading,
        preampDecision: context.preampDecision,
        preampPlacements: context.preampPlacements,
        preampAssets: context.preampAssets,
    };
}
/**
 * Get asset for a Preamp Placement.
 */
export function usePreampPlacement(placementNameOrId, transformFn) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('usePreampPlacement cannot be used outside of CohesionProvider');
    }
    const asset = useMemo(() => {
        const asset = context.preampPlacements[placementNameOrId] ?? null;
        if (transformFn instanceof Function) {
            return transformFn({
                preampErr: context.preampErr,
                preampIsLoading: context.preampIsLoading,
                asset,
            });
        }
        return asset;
    }, [
        placementNameOrId,
        transformFn,
        context.preampErr,
        context.preampIsLoading,
        context.preampPlacements,
    ]);
    return {
        preampErr: context.preampErr,
        preampIsLoading: context.preampIsLoading,
        asset,
    };
}
/**
 * Get whether a Preamp Placement has been placed.
 */
export function usePreampPlacementExists(placementNameOrId) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('usePreampPlacementExists cannot be used outside of CohesionProvider');
    }
    const placementExists = !!context.preampPlacements[placementNameOrId];
    return placementExists;
}
/**
 * Get a Preamp Asset.
 */
export function usePreampAsset(assetNameIdOrFn, transformFn) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('usePreampAsset cannot be used outside of CohesionProvider');
    }
    const asset = useMemo(() => {
        const asset = context.preampAssets.find((asset) => {
            if (assetNameIdOrFn instanceof Function) {
                return assetNameIdOrFn(asset);
            }
            return (asset.assetName === assetNameIdOrFn ||
                asset.assetId === assetNameIdOrFn);
        }) ?? null;
        if (transformFn instanceof Function) {
            return transformFn({
                preampErr: context.preampErr,
                preampIsLoading: context.preampIsLoading,
                asset,
            });
        }
        return asset;
    }, [
        assetNameIdOrFn,
        transformFn,
        context.preampErr,
        context.preampIsLoading,
        context.preampAssets,
    ]);
    return {
        preampErr: context.preampErr,
        preampIsLoading: context.preampIsLoading,
        asset,
    };
}
/**
 * Get whether a Preamp Asset has been placed.
 */
export function usePreampAssetExists(assetNameIdOrFn) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('usePreampAssetExists cannot be used outside of CohesionProvider');
    }
    const assetExists = useMemo(() => {
        const asset = context.preampAssets.find((asset) => {
            if (assetNameIdOrFn instanceof Function) {
                return assetNameIdOrFn(asset);
            }
            return (asset.assetName === assetNameIdOrFn || asset.assetId === assetNameIdOrFn);
        });
        return !!asset;
    }, [assetNameIdOrFn, context.preampAssets]);
    return assetExists;
}
export const MonarchSlotContext = createContext(undefined);
export function MonarchSlot({ children = _jsx(_Fragment, {}), slotId, components, fallback = _jsx(_Fragment, {}), }) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('MonarchSlot cannot be used outside of CohesionProvider');
    }
    const { err, isLoading, result, data } = useMonarchSlot(slotId);
    const contextValue = useMemo(() => {
        return { err, isLoading, result, data };
    }, [slotId, err, isLoading, result, data]);
    if (isLoading) {
        return (_jsx(MonarchSlotContext.Provider, { value: contextValue, children: fallback }));
    }
    const componentCustomId = result?.value?.componentCustomId ?? '';
    const Component = components
        ? components[componentCustomId]
        : context.components[componentCustomId];
    return (_jsx(MonarchSlotContext.Provider, { value: contextValue, children: _jsx(Suspense, { fallback: fallback, children: Component ? _jsx(Component, {}) : children }) }));
}
export const MonarchPreampPlacementContext = createContext(undefined);
export function MonarchPreampPlacement({ children = _jsx(_Fragment, {}), placement, components, componentId, fallback = _jsx(_Fragment, {}), }) {
    const context = useContext(CohesionContext);
    if (!context) {
        throw new Error('MonarchPreampPlacement cannot be used outside of CohesionProvider');
    }
    const { err, isLoading, asset, data } = useMonarchPreampPlacement(placement);
    const contextValue = useMemo(() => {
        return { err, isLoading, asset, data };
    }, [placement, err, isLoading, asset, data]);
    if (isLoading) {
        return (_jsx(MonarchPreampPlacementContext.Provider, { value: contextValue, children: fallback }));
    }
    const componentCustomId = (componentId instanceof Function ? componentId(asset) : asset?.assetName) ??
        '';
    const Component = components
        ? components[componentCustomId]
        : context.components[componentCustomId];
    return (_jsx(MonarchPreampPlacementContext.Provider, { value: contextValue, children: _jsx(Suspense, { fallback: fallback, children: Component ? _jsx(Component, {}) : children }) }));
}
